import { useState } from 'react';
import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, TextField as MuiTextField, Typography } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CurrencyInput from '../../../components/shared/CurrencyInput';

// Pull Credit Modal
export const PullCreditModal = ({ show, onClose, onSubmit, borrowerName }) => {
  const [creditType, setCreditType] = useState('hard');
  const [borrowerScope, setBorrowerScope] = useState('individual');
  const [isReissue, setIsReissue] = useState(false);
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ creditType, borrowerScope, isReissue, reason, timestamp: new Date().toISOString() });
  };

  if (!show) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Pull Credit Report</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>Pulling credit for: <strong>{borrowerName}</strong></Alert>
          <MuiTextField select label="Credit Type" size="small" fullWidth required value={creditType} onChange={(e) => setCreditType(e.target.value)} helperText="Soft pulls do not affect credit score. Hard pulls are required for final approval." sx={{ mb: 2 }}>
            <MenuItem value="soft">Soft Pull (No impact on score)</MenuItem>
            <MenuItem value="hard">Hard Pull (Standard inquiry)</MenuItem>
          </MuiTextField>
          <MuiTextField select label="Borrower Scope" size="small" fullWidth required value={borrowerScope} onChange={(e) => setBorrowerScope(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="individual">Individual</MenuItem>
            <MenuItem value="joint">Joint (All borrowers)</MenuItem>
          </MuiTextField>
          <FormControlLabel control={<Checkbox checked={isReissue} onChange={(e) => setIsReissue(e.target.checked)} />} label="This is a re-issue" />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>Check if this replaces a previous credit pull</Typography>
          <MuiTextField label="Reason for Pull (Optional)" size="small" fullWidth multiline rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g., Initial application, rate refinance, etc." />
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="contained">Pull Credit Report</Button></DialogActions>
      </form>
    </Dialog>
  );
};

// Order Appraisal Modal
export const OrderAppraisalModal = ({ show, onClose, onSubmit, property }) => {
  const [appraisalType, setAppraisalType] = useState('full');
  const [propertyType, setPropertyType] = useState(property?.propertyType || 'SingleFamily');
  const [occupancy, setOccupancy] = useState(property?.occupancy || 'PrimaryResidence');
  const [estimatedValue, setEstimatedValue] = useState(property?.estimatedValue || '');
  const [loanType, setLoanType] = useState('Conventional');
  const [rushOrder, setRushOrder] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ appraisalType, propertyType, occupancy, estimatedValue: parseFloat(estimatedValue), loanType, rushOrder, timestamp: new Date().toISOString() });
  };

  if (!show) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Order Appraisal</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>Property: <strong>{property?.address || 'Address not set'}</strong></Alert>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <MuiTextField select label="Appraisal Type" size="small" fullWidth required value={appraisalType} onChange={(e) => setAppraisalType(e.target.value)}>
                <MenuItem value="full">Full Appraisal (Interior & Exterior)</MenuItem>
                <MenuItem value="drive-by">Drive-By (Exterior Only)</MenuItem>
                <MenuItem value="desktop">Desktop Appraisal</MenuItem>
                <MenuItem value="waiver">Appraisal Waiver (If eligible)</MenuItem>
              </MuiTextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MuiTextField select label="Property Type" size="small" fullWidth required value={propertyType} onChange={(e) => setPropertyType(e.target.value)}>
                <MenuItem value="Single Family">Single Family</MenuItem>
                <MenuItem value="Condo">Condo</MenuItem>
                <MenuItem value="Townhouse">Townhouse</MenuItem>
                <MenuItem value="Multi-Family">Multi-Family (2-4 units)</MenuItem>
              </MuiTextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MuiTextField select label="Occupancy" size="small" fullWidth required value={occupancy} onChange={(e) => setOccupancy(e.target.value)}>
                <MenuItem value="Primary Residence">Primary Residence</MenuItem>
                <MenuItem value="Second Home">Second Home</MenuItem>
                <MenuItem value="Investment">Investment Property</MenuItem>
              </MuiTextField>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}><CurrencyInput label="Estimated Value" size="small" fullWidth value={estimatedValue} onChange={setEstimatedValue} /></Grid>
          </Grid>
          <MuiTextField select label="Loan Type" size="small" fullWidth required value={loanType} onChange={(e) => setLoanType(e.target.value)} sx={{ mt: 2 }}>
            <MenuItem value="Conventional">Conventional</MenuItem>
            <MenuItem value="FHA">FHA</MenuItem>
            <MenuItem value="VA">VA</MenuItem>
            <MenuItem value="USDA">USDA</MenuItem>
            <MenuItem value="Jumbo">Jumbo</MenuItem>
          </MuiTextField>
          <FormControlLabel control={<Checkbox checked={rushOrder} onChange={(e) => setRushOrder(e.target.checked)} />} label="Rush Order (+$150 fee, 3-5 day turnaround)" sx={{ mt: 1 }} />
          <Alert severity="warning" sx={{ mt: 1 }}><strong>Estimated Cost:</strong> ${rushOrder ? 700 : 550}<br /><Typography variant="caption">Final cost may vary based on property complexity and location</Typography></Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="contained" startIcon={<SendIcon />}>Order Appraisal</Button></DialogActions>
      </form>
    </Dialog>
  );
};

// Open Title Modal
export const OpenTitleModal = ({ show, onClose, onSubmit, property, borrower }) => {
  const [transactionType, setTransactionType] = useState('purchase');
  const [titleCompany, setTitleCompany] = useState('');
  const [rushOrder, setRushOrder] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ transactionType, titleCompany, rushOrder, timestamp: new Date().toISOString() });
  };

  if (!show) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Open Title Order</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}><strong>Property:</strong> {property?.address || 'Address not set'}<br /><strong>Borrower:</strong> {borrower?.name || 'Not set'}</Alert>
          <MuiTextField select label="Transaction Type" size="small" fullWidth required value={transactionType} onChange={(e) => setTransactionType(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="purchase">Purchase</MenuItem>
            <MenuItem value="refinance">Refinance</MenuItem>
            <MenuItem value="cash-out-refinance">Cash-Out Refinance</MenuItem>
          </MuiTextField>
          <MuiTextField label="Preferred Title Company (Optional)" size="small" fullWidth value={titleCompany} onChange={(e) => setTitleCompany(e.target.value)} placeholder="Leave blank to use default provider" helperText="If left blank, we'll assign a title company from our network" sx={{ mb: 2 }} />
          <FormControlLabel control={<Checkbox checked={rushOrder} onChange={(e) => setRushOrder(e.target.checked)} />} label="Rush Order (5-7 day turnaround)" />
          <Alert severity="warning" sx={{ mt: 1 }}><strong>Note:</strong> Title company will contact you within 1 business day to confirm order details and schedule closing.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="contained" startIcon={<SendIcon />}>Open Title Order</Button></DialogActions>
      </form>
    </Dialog>
  );
};

// Order VOA (Verification of Assets) Modal
export const OrderVOAModal = ({ show, onClose, onSubmit, borrowerName }) => {
  const [verificationScope, setVerificationScope] = useState('all');
  const [accountTypes, setAccountTypes] = useState(['checking', 'savings']);

  const handleAccountTypeToggle = (type) => {
    setAccountTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ verificationScope, accountTypes, timestamp: new Date().toISOString() });
  };

  if (!show) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Order Digital Asset Verification (VOA)</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>Ordering VOA for: <strong>{borrowerName}</strong></Alert>
          <MuiTextField select label="Verification Scope" size="small" fullWidth required value={verificationScope} onChange={(e) => setVerificationScope(e.target.value)} sx={{ mb: 2 }}>
            <MenuItem value="all">All Accounts</MenuItem>
            <MenuItem value="selected">Selected Account Types</MenuItem>
          </MuiTextField>
          {verificationScope === 'selected' && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Account Types to Verify</Typography>
              <FormControlLabel control={<Checkbox size="small" checked={accountTypes.includes('checking')} onChange={() => handleAccountTypeToggle('checking')} />} label="Checking Accounts" />
              <FormControlLabel control={<Checkbox size="small" checked={accountTypes.includes('savings')} onChange={() => handleAccountTypeToggle('savings')} />} label="Savings Accounts" />
              <FormControlLabel control={<Checkbox size="small" checked={accountTypes.includes('investment')} onChange={() => handleAccountTypeToggle('investment')} />} label="Investment Accounts" />
              <FormControlLabel control={<Checkbox size="small" checked={accountTypes.includes('retirement')} onChange={() => handleAccountTypeToggle('retirement')} />} label="Retirement Accounts (401k, IRA)" />
            </>
          )}
          <Alert severity="success" sx={{ mt: 1 }}><strong>Digital Verification Benefits:</strong> Instant results, no manual upload, 90-day history, borrower connects via secure portal.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="contained" startIcon={<SendIcon />}>Order VOA</Button></DialogActions>
      </form>
    </Dialog>
  );
};

// Order VOIE (Verification of Income/Employment) Modal
export const OrderVOIEModal = ({ show, onClose, onSubmit, borrowerName, employer }) => {
  const [verificationType, setVerificationType] = useState('both');
  const [employerName, setEmployerName] = useState(employer || '');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ verificationType, employerName, timestamp: new Date().toISOString() });
  };

  if (!show) return null;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Order Digital Income/Employment Verification (VOIE)</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>Ordering VOIE for: <strong>{borrowerName}</strong></Alert>
          <MuiTextField label="Employer Name" size="small" fullWidth required value={employerName} onChange={(e) => setEmployerName(e.target.value)} placeholder="Enter employer name" sx={{ mb: 2 }} />
          <MuiTextField select label="Verification Type" size="small" fullWidth required value={verificationType} onChange={(e) => setVerificationType(e.target.value)}>
            <MenuItem value="both">Income & Employment</MenuItem>
            <MenuItem value="income">Income Only</MenuItem>
            <MenuItem value="employment">Employment Only</MenuItem>
          </MuiTextField>
          <Alert severity="success" sx={{ mt: 2 }}><strong>Digital Verification Benefits:</strong> Instant results, no manual paystub upload, 24-month income history, real-time employment status, borrower connects via secure payroll portal.</Alert>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Cancel</Button><Button type="submit" variant="contained" startIcon={<SendIcon />}>Order VOIE</Button></DialogActions>
      </form>
    </Dialog>
  );
};
